/** Core gameplay types shared between systems, UI and save data. */
import type { IconId, WeaponKind } from '../art/pixel/types';

/**
 * `set` items belong to an item set (Diablo 3 style) and sit beside legendaries.
 * `abyssal` is the top tier: it never rolls by chance and only drops in the Abyss.
 */
export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary' | 'set' | 'abyssal';
export const RARITIES: readonly Rarity[] = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'legendary',
  'set',
  'abyssal',
];

/** Damage elements. Physical damage is reduced by armor; elemental damage by resistances. */
export type Element = 'physical' | 'fire' | 'ice' | 'lightning' | 'poison';
export type DamageElement = Exclude<Element, 'physical'>;
export const DAMAGE_ELEMENTS: readonly DamageElement[] = ['fire', 'ice', 'lightning', 'poison'];

/** Armor weights (Diablo style): cloth trades toughness for Magic, plate the reverse. */
export type ArmorType = 'cloth' | 'leather' | 'mail' | 'plate';
export const ARMOR_TYPES: readonly ArmorType[] = ['cloth', 'leather', 'mail', 'plate'];
/** Gear that comes in armor types. */
export type ArmorSlot = 'helm' | 'shoulders' | 'armor' | 'bracers' | 'gloves' | 'boots' | 'belt' | 'legs';
export const ARMOR_SLOTS: readonly ArmorSlot[] = [
  'helm',
  'shoulders',
  'armor',
  'bracers',
  'gloves',
  'belt',
  'legs',
  'boots',
];
export const isArmorSlot = (slot: string): slot is ArmorSlot =>
  (ARMOR_SLOTS as readonly string[]).includes(slot);

/** Item types. Charms are never equipped: they work from the bag. */
export type Slot =
  | 'weapon'
  | 'helm'
  | 'shoulders'
  | 'armor'
  | 'bracers'
  | 'gloves'
  | 'belt'
  | 'legs'
  | 'boots'
  | 'ring'
  | 'amulet'
  /** Off hands: shields, and orbs/tomes for mages (a second weapon can go there too). */
  | 'shield'
  | 'focus'
  | 'charm';
/** Equippable item types (everything except charms). */
export type GearSlot = Exclude<Slot, 'charm'>;
export const GEAR_SLOTS: readonly GearSlot[] = [
  'weapon',
  'helm',
  'shoulders',
  'armor',
  'bracers',
  'gloves',
  'belt',
  'legs',
  'boots',
  'ring',
  'amulet',
  'shield',
  'focus',
];

/** Places on the hero's body where gear is worn (two ring fingers). */
export type EquipSlot =
  | 'weapon'
  | 'offhand'
  | 'helm'
  | 'shoulders'
  | 'amulet'
  | 'armor'
  | 'bracers'
  | 'gloves'
  | 'belt'
  | 'legs'
  | 'ring1'
  | 'ring2'
  | 'boots';
export const EQUIP_SLOTS: readonly EquipSlot[] = [
  'helm',
  'shoulders',
  'amulet',
  'armor',
  'weapon',
  'offhand',
  'bracers',
  'gloves',
  'ring1',
  'ring2',
  'belt',
  'legs',
  'boots',
];

/**
 * Which body slots an item type can go into. A weapon can be held in either hand (the
 * off hand only adds its stats); shields and mage focuses only go in the off hand.
 */
export function equipSlotsFor(slot: Slot): EquipSlot[] {
  if (slot === 'charm') return [];
  if (slot === 'ring') return ['ring1', 'ring2'];
  if (slot === 'weapon') return ['weapon', 'offhand'];
  if (slot === 'shield' || slot === 'focus') return ['offhand'];
  return [slot];
}

/** Off-hand weapons add this share of their base stats (they never attack). */
export const OFFHAND_WEAPON_BASE = 0.5;

export type GemType = 'ruby' | 'emerald' | 'topaz' | 'amethyst' | 'diamond';
/** A gem: quality 0 (Chipped) up to MAX_GEM_QUALITY (Royal). Three of a kind combine into one better gem. */
export interface Gem {
  type: GemType;
  q: number;
}
/** Gems give a different stat depending on what kind of gear they sit in. */
export type SocketGroup = 'weapon' | 'helm' | 'armor';

export type CharmSize = 'small' | 'large' | 'grand';

export type Difficulty = 'story' | 'normal' | 'hard' | 'nightmare';

/** Every numeric stat an actor can have. Percent-like stats are fractions (0.05 = 5%). */
export interface Stats {
  maxHp: number;
  maxMp: number;
  atk: number;
  def: number;
  mag: number;
  crit: number;
  critDmg: number;
  atkSpeed: number;
  moveSpeed: number;
  lifesteal: number;
  cdr: number;
  goldFind: number;
  magicFind: number;
  xpBonus: number;
  hpRegen: number;
  mpRegen: number;
  burnChance: number;
  skillDmg: number;
  dmgBonus: number;
  dodgeCost: number;
  /** Bonus to the elemental part of your damage. */
  elemDmg: number;
}

export type StatKey = keyof Stats;

export const ZERO_STATS: Readonly<Stats> = Object.freeze({
  maxHp: 0,
  maxMp: 0,
  atk: 0,
  def: 0,
  mag: 0,
  crit: 0,
  critDmg: 0,
  atkSpeed: 0,
  moveSpeed: 0,
  lifesteal: 0,
  cdr: 0,
  goldFind: 0,
  magicFind: 0,
  xpBonus: 0,
  hpRegen: 0,
  mpRegen: 0,
  burnChance: 0,
  skillDmg: 0,
  dmgBonus: 0,
  dodgeCost: 0,
  elemDmg: 0,
});

/** How to display a stat in the UI. */
export const STAT_INFO: Record<StatKey, { label: string; pct: boolean }> = {
  maxHp: { label: 'Max HP', pct: false },
  maxMp: { label: 'Max MP', pct: false },
  atk: { label: 'Attack', pct: false },
  def: { label: 'Defense', pct: false },
  mag: { label: 'Magic', pct: false },
  crit: { label: 'Crit Chance', pct: true },
  critDmg: { label: 'Crit Damage', pct: true },
  atkSpeed: { label: 'Attack Speed', pct: true },
  moveSpeed: { label: 'Move Speed', pct: true },
  lifesteal: { label: 'Lifesteal', pct: true },
  cdr: { label: 'Cooldown Red.', pct: true },
  goldFind: { label: 'Gold Find', pct: true },
  magicFind: { label: 'Magic Find', pct: true },
  xpBonus: { label: 'XP Bonus', pct: true },
  hpRegen: { label: 'HP Regen/s', pct: false },
  mpRegen: { label: 'MP Regen/s', pct: false },
  burnChance: { label: 'Burn Chance', pct: true },
  skillDmg: { label: 'Skill Damage', pct: true },
  dmgBonus: { label: 'Damage', pct: true },
  dodgeCost: { label: 'Dodge Cost', pct: true },
  elemDmg: { label: 'Elemental Damage', pct: true },
};

export type LegendaryId =
  | 'thornfang'
  | 'mountain_heart'
  | 'dragonbreath'
  | 'seraphs_tear'
  | 'hollow_crown'
  | 'windwalkers'
  | 'phoenix_band'
  | 'stormcaller'
  | 'vampire_kiss'
  | 'echo_blade'
  // weapons
  | 'frostreaver'
  | 'venomfang'
  | 'lightbringer'
  | 'sunstrike'
  | 'galeforce'
  | 'siegebreaker'
  | 'thunderlord'
  | 'pestilence'
  // armor and jewelry
  | 'robe_archmage'
  | 'aegis_dawn'
  | 'starfall'
  | 'mask_berserker'
  | 'pyres_grip'
  | 'stormweave'
  | 'emberstride'
  | 'tempest_sash'
  | 'bladedancer'
  | 'sprinters_oath'
  | 'band_ascendant'
  | 'serpent_eye'
  // shoulders and bracers
  | 'titan_pauldrons'
  | 'magus_mantle'
  | 'viper_bracers'
  | 'stormguard_bracers'
  // legs and off hands
  | 'stride_of_ages'
  | 'colossus_legplates'
  | 'aegis_of_ages'
  | 'thornwall'
  | 'orb_of_infinity'
  | 'tome_of_storms';

export type SetId =
  | 'warden'
  | 'whisperwind'
  | 'archmage'
  | 'stormlord'
  | 'emberforged'
  | 'venomcaller'
  | 'frostbound'
  | 'deadeye';

export interface Affix {
  stat: StatKey;
  value: number;
}

export interface Item {
  uid: string;
  slot: Slot;
  /** Weapon kind for weapons. */
  kind?: WeaponKind;
  /** Visual/material tier 0-5 (derived from item level). */
  tier: number;
  ilvl: number;
  rarity: Rarity;
  name: string;
  /** Implicit stats from the base type (scaled by upgrade level). */
  base: Partial<Stats>;
  affixes: Affix[];
  /** Blacksmith upgrade level 0..10. */
  upgrade: number;
  legendary?: LegendaryId;
  /** Set items: the set this piece belongs to. */
  set?: SetId;
  /** Weapons: the element part of the damage is converted to (see elemConv). */
  element?: DamageElement;
  /** Weapons: fraction of physical damage converted to `element` (0-1). */
  elemConv?: number;
  /** Helms, armor, gloves, boots and belts. Older items have none and count as mail. */
  armorType?: ArmorType;
  locked?: boolean;
  isNew?: boolean;
  /** Charms only. */
  charmSize?: CharmSize;
  /** Cursed charms: stronger bonuses plus a drawback (a negative affix). */
  cursed?: boolean;
  /** Charms only: set aside in the bag (never active, even with free charm slots). */
  charmOff?: boolean;
  /** Gem sockets (null = empty). Only gear from the Abyss has them. */
  sockets?: (Gem | null)[];
  /** Bag items: the bag cell it sits in (see layoutBag). */
  bagSlot?: number;
}

export type ConsumableId = 'elixir' | 'phoenix' | 'tonic_might' | 'tonic_guard' | 'tonic_fortune';
export type MaterialId = 'dust' | 'herb' | 'pelt' | 'crystal' | 'ember' | 'frost' | 'void';

export interface ConsumableDef {
  id: ConsumableId;
  name: string;
  icon: IconId;
  desc: string;
  price: number;
}

export interface MaterialDef {
  id: MaterialId;
  name: string;
  icon: IconId;
  desc: string;
  sell: number;
}
